import mapValues from 'lodash/mapValues';
import get from 'lodash/get';
import { request } from '../../lib/Actions';
import { fetchRequest, Mappings } from '../../lib/LeanLinkService';
import { failure, success } from '../../lib/Actions/factory';
import * as normalizers from './normalizers';

import * as actions from './actions';
import * as paginatedActions from '../PaginatedItems/actions';
import { ListTypes } from '../schemas';

/**
 * Request deduplication and short-term caching for GET requests.
 *
 * Two-tier deduplication:
 * 1. In-flight deduplication: Concurrent requests share the same promise
 * 2. Short-term cache (TTL): Recently completed requests return cached response
 *
 * Behavior:
 * - Concurrent requests to the same URL share the same promise
 * - Requests within TTL window of a completed request return cached response
 * - Requests after TTL expires make fresh requests
 *
 * This prevents duplicate requests during initial page load when multiple
 * components mount within a short time window, while still allowing
 * explicit refresh requests after the TTL expires.
 */
const inFlightGetRequests = new Map();

/**
 * Short-term cache for completed GET requests.
 * Stores { response, timestamp } for each URL.
 * Entries are automatically cleaned up when TTL expires.
 */
const recentGetResponses = new Map();

// Time-to-live for cached responses (in milliseconds)
// 2s covers most (if not all) component mounting in page
const RESPONSE_CACHE_TTL = 2000;

/**
 * Check if a cached response is still valid
 */
function getCachedResponse(url) {
  const cached = recentGetResponses.get(url);
  if (!cached) {
    return null;
  }

  const age = Date.now() - cached.timestamp;
  if (age > RESPONSE_CACHE_TTL) {
    // Cache expired, clean up
    recentGetResponses.delete(url);
    return null;
  }

  return cached.response;
}

/**
 * Store a response in the short-term cache
 */
function cacheResponse(url, response) {
  recentGetResponses.set(url, {
    response,
    timestamp: Date.now(),
  });

  // Schedule cleanup after TTL
  setTimeout(() => {
    const cached = recentGetResponses.get(url);
    if (cached && Date.now() - cached.timestamp >= RESPONSE_CACHE_TTL) {
      recentGetResponses.delete(url);
    }
  }, RESPONSE_CACHE_TTL + 100);
}

/**
 * Runs a request towards the API. The store will get updated by means of the existing
 * actions BUT this will also return a resolvable Promise.
 */
const runner = mapping => action => dispatch => (requestPayload) => {
  const {
    url,
    fetchUrl,
    entity,
    normalization = true, // Flag to turn off the normalization of the response data
    skipDeduplication = false, // Flag to bypass deduplication for this specific request
    skipCache = false, // Flag to bypass short-term cache (force fresh fetch)
    ...context
  } = requestPayload;

  if (!url) {
    throw new Error('url must be provided');
  }

  const effectiveUrl = fetchUrl || url;

  // Only deduplicate/cache GET requests (FETCH action) that don't have skip flags
  const shouldDeduplicate = action === actions.FETCH && !skipDeduplication;
  const shouldCache = action === actions.FETCH && !skipCache && !skipDeduplication;

  // Tier 1: Check if an identical request is already in-flight
  if (shouldDeduplicate && inFlightGetRequests.has(effectiveUrl)) {
    return inFlightGetRequests.get(effectiveUrl);
  }

  // Tier 2: Check short-term cache for recently completed requests
  if (shouldCache) {
    const cachedResponse = getCachedResponse(effectiveUrl);
    if (cachedResponse) {
      // Return cached response immediately as a resolved promise
      return Promise.resolve(cachedResponse);
    }
  }

  dispatch(request(action)(requestPayload));

  const requestPromise = new Promise((resolve, reject) => {
    fetchRequest(
      mapping({ url: effectiveUrl, payload: entity }),
      (data) => {
        const maybeNormalizedData = (data !== undefined && normalization)
          ? normalizers.entity(data) : data;
        const listResponse = action === actions.FETCH && ListTypes.includes(
          get(maybeNormalizedData, 'result._type'),
        );
        const sendAction = listResponse ? paginatedActions.FETCH : action;
        dispatch(success(sendAction, { ...context, url })(maybeNormalizedData));

        // Clean up in-flight tracking and cache the response
        if (shouldDeduplicate) {
          inFlightGetRequests.delete(effectiveUrl);
        }
        if (shouldCache) {
          cacheResponse(effectiveUrl, maybeNormalizedData);
        }

        resolve(maybeNormalizedData);
      },
      (error) => {
        dispatch(failure(action, { ...context, url })(error.errors));

        // Clean up in-flight tracking (don't cache errors)
        if (shouldDeduplicate) {
          inFlightGetRequests.delete(effectiveUrl);
        }

        reject(error);
      },
    );
  });

  // Track in-flight GET requests for deduplication
  if (shouldDeduplicate) {
    inFlightGetRequests.set(effectiveUrl, requestPromise);
  }

  return requestPromise;
};

const clientHelpers = ({
  fetch: runner(Mappings.get)(actions.FETCH),
  create: runner(Mappings.post)(actions.CREATE),
  patch: runner(Mappings.patch)(actions.PATCH),
  put: runner(Mappings.put)(actions.PUT),
  del: runner(Mappings.del)(actions.DELETE),
});

const bindDispatch = (helpers, dispatch) => mapValues(helpers, helper => helper(dispatch));

export const client = dispatch => bindDispatch(clientHelpers, dispatch);
export const shouldSkip = skip => props => (skip === true || (typeof skip === 'function' && skip(props) === true));
